import React from 'react';
import type { FieldPath, FieldValues, PathValue } from 'react-hook-form';
import type { CustomValueWarningOptions } from '@aurora/shared-types/customValueWarning';

interface Props<NameT extends FieldPath<DataT>, DataT> {
  /** The options supplied _to_ the field */
  options: CustomValueWarningOptions<PathValue<DataT, NameT>>;

  /** The default logic supplied _by_ the field. */
  defaultLogic: (value: PathValue<DataT, NameT>) => boolean;

  /** The current value of the field */
  value: PathValue<DataT, NameT>;
}

/**
 * Determines whether to render a custom value warning
 * Should be used directly in field components -- use {@link CustomValueWarningComponent} within the options themselves
 * @uathor Rosalyn Rowe
 */
const CustomValueWarningWrapper = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  options,
  defaultLogic,
  value
}: Props<NameT, DataT>): React.ReactElement => {
  const { component: Component, customLogic } = options ?? {};

  // Nothing to render
  if (!Component) {
    return null;
  }

  const shouldRender = customLogic ? customLogic(value) : defaultLogic(value);

  // No reason to render
  if (!shouldRender) {
    return null;
  }

  return <Component />;
};

export default CustomValueWarningWrapper;
